import React from 'react'
import { Card, CardContent, CardHeader, Label, Switch, Input } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  getPublishStabilityEnabled,
  setPublishStabilityEnabled,
  getMinorRSIRetargetEnabled,
  setMinorRSIRetargetEnabled,
  getMinorRSIEarlyConfirmEnabled,
  setMinorRSIEarlyConfirmEnabled,
  getStrictPublishEnabled,
  setStrictPublishEnabled,
  getBlockUnpublishEnabled,
  setBlockUnpublishEnabled,
  getSignalsMultiTfGuardEnabled,
  setSignalsMultiTfGuardEnabled,
  getPublishMaxAgeSettings,
  setPublishMaxAgeSettings,
  type PublishMaxAgeSettings,
} from '../services/opportunityEngineSettingsService'

export default function ManageOpportunitiesSettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [stability, setStability] = React.useState(true)
  const [minorRetarget, setMinorRetarget] = React.useState(false)
  const [minorEarlyConfirm, setMinorEarlyConfirm] = React.useState(true)
  const [strictPublish, setStrictPublish] = React.useState(true)
  const [blockUnpublish, setBlockUnpublish] = React.useState(true)
  const [signalsMultiTfGuard, setSignalsMultiTfGuard] = React.useState(false)
  const [maxAge, setMaxAge] = React.useState<PublishMaxAgeSettings | null>(null)

  React.useEffect(() => {
    let alive = true
    Promise.all([
      getPublishStabilityEnabled(),
      getMinorRSIRetargetEnabled(),
      getMinorRSIEarlyConfirmEnabled(),
      getStrictPublishEnabled(),
      getBlockUnpublishEnabled(),
      getSignalsMultiTfGuardEnabled(),
      getPublishMaxAgeSettings(),
    ])
      .then(([st, mr, me, sp, bu, guard, ages]) => {
        if (!alive) return
        setStability(!!st)
        setMinorRetarget(!!mr)
        setMinorEarlyConfirm(!!me)
        setStrictPublish(!!sp)
        setBlockUnpublish(!!bu)
        setSignalsMultiTfGuard(!!guard)
        setMaxAge(ages)
      })
      .catch((e) =>
        toast({
          title: 'Gerenciar Oportunidades',
          description: e?.message || 'Falha ao carregar',
          variant: 'error',
        }),
      )
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [toast])

  async function toggleStability(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setPublishStabilityEnabled(next)
      setStability(next)
      toast({
        title: 'Estabilidade na publicação',
        description: next ? 'Ativada (histerese aplicada)' : 'Desativada (publicação imediata)',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Estabilidade na publicação',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function toggleMinorRetarget(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setMinorRSIRetargetEnabled(next)
      setMinorRetarget(next)
      toast({
        title: 'Retarget do RSI (TF menor)',
        description: next ? 'Ativado' : 'Desativado',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Retarget do RSI (TF menor)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function toggleMinorEarlyConfirm(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setMinorRSIEarlyConfirmEnabled(next)
      setMinorEarlyConfirm(next)
      toast({
        title: 'Confirmação antecipada por RSI (TF menor)',
        description: next
          ? 'Ativada para TF mandantes de 60 a M'
          : 'Desativada (aguarda apenas confirmação estrutural por fechamento)',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Confirmação antecipada por RSI (TF menor)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function toggleStrictPublish(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setStrictPublishEnabled(next)
      setStrictPublish(next)
      toast({
        title: 'Confirmação estrita de oportunidades',
        description: next
          ? 'Ativada (idade máxima + rompimento do TF mandante)'
          : 'Desativada (mantém validação atual)',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Confirmação estrita de oportunidades',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function toggleBlockUnpublish(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setBlockUnpublishEnabled(next)
      setBlockUnpublish(next)
      toast({
        title: 'Blindagem de oportunidades ativas',
        description: next
          ? 'Ativada: impede despublicar oportunidades ativas confirmadas por processos automáticos.'
          : 'Desativada: permite despublicar oportunidades ativas em fluxos técnicos.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Blindagem de oportunidades ativas',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function toggleSignalsMultiTfGuard(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setSignalsMultiTfGuardEnabled(next)
      setSignalsMultiTfGuard(next)
      toast({
        title: 'Guarda multi-TF do m\u00f3dulo de Sinais',
        description: next
          ? 'Ativada: impede publicar TFs mandantes quando houver oportunidade ativa no TF menor direto na mesma dire\u00e7\u00e3o.'
          : 'Desativada: publica\u00e7\u00e3o n\u00e3o guarda sequ\u00eancia entre TFs mandantes.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Guarda multi-TF do m\u00f3dulo de Sinais',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function saveMaxAge(next: PublishMaxAgeSettings) {
    if (saving) return
    setSaving(true)
    try {
      await setPublishMaxAgeSettings(next)
      setMaxAge(next)
      toast({
        title: 'Idade máxima por TF mandante',
        description: 'Limites atualizados com sucesso.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Idade máxima por TF mandante',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  function handleMaxAgeChange(field: keyof PublishMaxAgeSettings, value: string) {
    if (!maxAge) return
    const n = Number.parseInt(value, 10)
    const safe = Number.isFinite(n) && n > 0 ? n : 0
    setMaxAge({ ...maxAge, [field]: safe })
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Gerenciar Oportunidades</CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Estabilidade na publicação (histerese)</div>
            <div className="text-sm text-foreground/80">
              Quando desativada, candidatos elegíveis são publicados sem aguardar janela de histerese.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label>Imediata</Label>
            <Switch
              checked={stability}
              onChange={(e) => toggleStability((e.target as HTMLInputElement).checked)}
              disabled={loading || saving}
            />
            <Label>Estável</Label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Blindagem de oportunidades ativas confirmadas</div>
            <div className="text-sm text-foreground/80">
              Quando ativada, impede que oportunidades ativas e já publicadas sejam despublicadas automaticamente.
              O fechamento continua sendo feito apenas pelas regras oficiais do módulo de Sinais ou por ações manuais autorizadas.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label>Permissivo</Label>
            <Switch
              checked={blockUnpublish}
              onChange={(e) => toggleBlockUnpublish((e.target as HTMLInputElement).checked)}
              disabled={loading || saving}
            />
            <Label>Blindado</Label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Sequ\u00eancia multi-TF (m\u00f3dulo de Sinais)</div>
            <div className="text-sm text-foreground/80">
              Quando ativada, impede publicar oportunidades em TFs mandantes (240/D/W/M) se ainda existir oportunidade ativa e publicada
              no TF menor direto na mesma dire\u00e7\u00e3o (ex.: 60 antes de 240, 240 antes de D).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label>Flex\u00edvel</Label>
            <Switch
              checked={signalsMultiTfGuard}
              onChange={(e) => toggleSignalsMultiTfGuard((e.target as HTMLInputElement).checked)}
              disabled={loading || saving}
            />
            <Label>Sequencial</Label>
          </div>
        </div>

        {maxAge && (
          <div className="flex flex-col gap-3 rounded-lg border border-border/40 p-3">
            <div className="mb-1 text-sm font-medium">Idade máxima por TF mandante (dias)</div>
            <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3 md:grid-cols-4">
              <div className="flex items-center justify-between gap-2">
                <span>1h (60)</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.tf60 || ''}
                  onChange={(e) => handleMaxAgeChange('tf60', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>4h (240)</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.tf240 || ''}
                  onChange={(e) => handleMaxAgeChange('tf240', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>12h (720)</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.tf720 || ''}
                  onChange={(e) => handleMaxAgeChange('tf720', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>1D (D)</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.tfD || ''}
                  onChange={(e) => handleMaxAgeChange('tfD', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>1W (W)</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.tfW || ''}
                  onChange={(e) => handleMaxAgeChange('tfW', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>1M (M)</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.tfM || ''}
                  onChange={(e) => handleMaxAgeChange('tfM', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>Default</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16 px-2 text-right text-xs"
                  value={maxAge.defaultDays || ''}
                  onChange={(e) => handleMaxAgeChange('defaultDays', e.target.value)}
                  disabled={loading || saving}
                />
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => maxAge && saveMaxAge(maxAge)}
                className="inline-flex h-8 items-center rounded bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                disabled={loading || saving || !maxAge}
              >
                Salvar limites
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Confirmação estrita de oportunidades</div>
            <div className="text-sm text-foreground/80">
              Quando ativada, só confirma/publica oportunidades dentro da idade máxima por TF mandante
              e com rompimento válido da correção no TF mandante (SSOT com as regras de tendência).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label>Flexível</Label>
            <Switch
              checked={strictPublish}
              onChange={(e) => toggleStrictPublish((e.target as HTMLInputElement).checked)}
              disabled={loading || saving}
            />
            <Label>Estrito</Label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Retarget do RSI para o TF menor do mandante final</div>
            <div className="text-sm text-foreground/80">
              Ex.: 1h→4h implica 5m→15m. Publica somente quando o RSI do TF menor final atende ao limiar sequencial.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label>Desativado</Label>
            <Switch
              checked={minorRetarget}
              onChange={(e) => toggleMinorRetarget((e.target as HTMLInputElement).checked)}
              disabled={loading || saving}
            />
            <Label>Ativado</Label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Confirmação antecipada por RSI do TF gatilho</div>
            <div className="text-sm text-foreground/80">
              Quando ativada, permite confirmar oportunidades em TF mandantes (60 a M) usando o RSI live do TF menor
              correspondente (39/61), mesmo antes do rompimento estrutural por fechamento, desde que a correção esteja ativa no mandante.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label>Estrutural apenas</Label>
            <Switch
              checked={minorEarlyConfirm}
              onChange={(e) => toggleMinorEarlyConfirm((e.target as HTMLInputElement).checked)}
              disabled={loading || saving}
            />
            <Label>RSI antecipado</Label>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
